import type { IExercise, DifficultyLevel } from './exercises';

function inferTargetMuscles(ex: IExercise): string[] {
  if (ex.targetMuscles && ex.targetMuscles.length > 0) return ex.targetMuscles;
  if (ex.isWarmup) return ['全身激活肌群'];
  if (ex.isStretch) return ['全身拉伸肌群'];

  const name = ex.name;
  const part = ex.bodyPart;
  const sub = ex.subCategory;
  const muscles: string[] = [];

  // ===== 胸部 =====
  if (part === 'chest') {
    if (name.includes('上斜') || sub === 'upper-chest') muscles.push('上胸肌');
    if (name.includes('下斜') || sub === 'lower-chest' || name.includes('下胸')) muscles.push('下胸肌');
    if (name.includes('夹胸') || name.includes('飞鸟') || sub === 'inner-chest' || name.includes('中缝')) muscles.push('胸大肌中缝');
    if (name.includes('卧推') || name.includes('推胸') || sub === 'mid-chest') muscles.push('胸大肌');
    if (name.includes('俯卧撑') || name.includes('臂屈伸')) muscles.push('胸大肌');
    if (name.includes('前锯肌')) muscles.push('前锯肌');
    if (muscles.length === 0) muscles.push('胸大肌');
    // 推类动作共同协同肌
    if (name.includes('推') || name.includes('卧推') || name.includes('俯卧撑')) muscles.push('三角肌前束', '肱三头肌');
    return Array.from(new Set(muscles));
  }

  // ===== 背部 =====
  if (part === 'back') {
    if (name.includes('下拉') || sub === 'lat' || name.includes('引体')) muscles.push('背阔肌');
    if (name.includes('划船') || sub === 'row' || name.includes('T杆')) muscles.push('背阔肌', '菱形肌', '斜方肌中下部');
    if (name.includes('硬拉')) muscles.push('竖脊肌', '臀大肌', '腘绳肌');
    if (name.includes('耸肩') || sub === 'traps') muscles.push('斜方肌上部');
    if (name.includes('面拉') || name.includes('反向飞鸟')) muscles.push('后三角肌', '斜方肌中下部', '菱形肌');
    if (name.includes('直臂下压')) muscles.push('背阔肌', '大圆肌');
    if (muscles.length === 0) muscles.push('背阔肌');
    if (name.includes('拉') || name.includes('划船') || name.includes('下拉')) muscles.push('肱二头肌');
    return Array.from(new Set(muscles));
  }

  // ===== 肩部 =====
  if (part === 'shoulders') {
    if (name.includes('前束') || sub === 'front-deltoid' || name.includes('实力推') || name.includes('推举')) muscles.push('三角肌前束');
    if (name.includes('侧平举') || name.includes('侧束') || sub === 'side-deltoid') muscles.push('三角肌中束');
    if (name.includes('后束') || name.includes('面拉') || sub === 'rear-deltoid') muscles.push('三角肌后束');
    if (name.includes('推') || name.includes('推举') || name.includes('肩推')) muscles.push('三角肌前束', '三角肌中束');
    if (muscles.length === 0) muscles.push('三角肌');
    if (name.includes('推') || name.includes('推举')) muscles.push('肱三头肌');
    return Array.from(new Set(muscles));
  }

  // ===== 腿部 =====
  if (part === 'legs') {
    if (name.includes('深蹲') || name.includes('腿举') || name.includes('腿屈伸') || sub === 'quads' || name.includes('蹲')) muscles.push('股四头肌');
    if (name.includes('硬拉') || name.includes('腿弯举') || name.includes('腘绳') || sub === 'hamstrings') muscles.push('腘绳肌');
    if (name.includes('臀') || name.includes('髋外展') || sub === 'glutes') muscles.push('臀大肌');
    if (name.includes('箭步') || name.includes('弓步') || name.includes('箭步蹲')) muscles.push('股四头肌', '臀大肌');
    if (muscles.length === 0) muscles.push('股四头肌');
    if (name.includes('深蹲') || name.includes('硬拉') || name.includes('腿举')) muscles.push('臀大肌');
    return Array.from(new Set(muscles));
  }

  // ===== 手臂 =====
  if (part === 'arms') {
    if (sub === 'biceps' || name.includes('弯举') || name.includes('二头')) muscles.push('肱二头肌');
    if (sub === 'triceps' || name.includes('臂屈伸') || name.includes('三头') || name.includes('下压')) muscles.push('肱三头肌');
    if (name.includes('前臂') || name.includes('腕弯')) muscles.push('前臂肌群');
    if (muscles.length === 0) muscles.push('手臂肌群');
    return Array.from(new Set(muscles));
  }

  // ===== 腹部/核心 =====
  if (part === 'abs') {
    if (name.includes('卷腹') || name.includes('仰卧起坐') || sub === 'upper-abs') muscles.push('腹直肌上部');
    if (name.includes('抬腿') || name.includes('举腿') || sub === 'lower-abs') muscles.push('腹直肌下部');
    if (name.includes('侧') || name.includes('转体') || sub === 'obliques') muscles.push('腹斜肌');
    if (name.includes('平板') || name.includes('支撑') || sub === 'core') muscles.push('核心肌群');
    if (muscles.length === 0) muscles.push('腹直肌');
    return Array.from(new Set(muscles));
  }

  // ===== 小腿 =====
  if (part === 'calves') {
    muscles.push('腓肠肌', '比目鱼肌');
    return muscles;
  }

  // 兜底
  const partLabels: Record<string, string> = { chest: '胸部', back: '背部', shoulders: '肩部', legs: '腿部', arms: '手臂', abs: '腹部', warmup: '热身', stretch: '拉伸', calves: '小腿' };
  return [partLabels[part] || '目标肌群'];
}

function inferDifficulty(ex: IExercise): DifficultyLevel {
  if (ex.difficulty) return ex.difficulty;

  const name = ex.name;
  const part = ex.bodyPart;
  const eq = ex.equipment;

  // 热身/拉伸都是入门
  if (ex.isWarmup || ex.isStretch) return 'beginner';

  // 徒手入门级
  if (ex.isBodyweight && (name.includes('跪姿') || name.includes('简易') || name.includes('入门'))) return 'beginner';

  // 复合动作（大重量基础）= 进阶
  if (
    name.includes('杠铃') && (
      name.includes('卧推') || name.includes('深蹲') || name.includes('硬拉') ||
      name.includes('推举') || name.includes('划船') || name.includes('实力推')
    )
  ) return 'advanced';

  // 自由重量复合动作 = 进阶
  if (name.includes('硬拉') || name.includes('深蹲') || name.includes('抓举') || name.includes('挺举')) return 'advanced';

  // 固定器械 = 入门（对新手友好）
  if (eq === 'machine') return 'beginner';

  // 徒手基础动作
  if (ex.isBodyweight && (name.includes('俯卧撑') || name.includes('深蹲') || name.includes('卷腹'))) return 'beginner';

  // 孤立动作（哑铃/绳索单关节）= 中级
  if (eq === 'dumbbell' && (name.includes('飞鸟') || name.includes('侧平举') || name.includes('弯举') || name.includes('臂屈伸'))) return 'intermediate';
  if (eq === 'cable' && (name.includes('夹胸') || name.includes('下压') || name.includes('面拉'))) return 'intermediate';

  // 热身/拉伸部位 = 入门
  if (part === 'warmup' || part === 'stretch') return 'beginner';

  // 腹部/小腿大多数动作 = 入门~中级
  if (part === 'abs' || part === 'calves') return 'beginner';

  // 兜底：自由重量 = 中级，器械 = 入门
  if (eq === 'barbell' || eq === 'dumbbell') return 'intermediate';
  return 'beginner';
}


export function enrichExercise(exercise: IExercise): IExercise {
  return { ...exercise, targetMuscles: inferTargetMuscles(exercise), difficulty: inferDifficulty(exercise) };
}
